import { useEffect, useMemo, useState } from "react"
import { EditableFormField, EditablePanel, FieldType, ModalId, UIPermission, useUIActions } from "../../../../../../features/ui"
import { useSystemStateHooks } from "../../../../../system"
import { useActions } from "./actions"
import { TableName } from "../../../../../../tables"
import { Data } from "../../../../../../features/ui"
import { LinkedProductsTable } from "../linked-products-table/linkedProductsTable"
import { LinkedProductsFormModal } from "../linked-products-form-modal/linkedProductsFormModal"
import { useProductLoaders } from "../../../../loaders/productLoaders"
import { Product, ProductType } from "../../../../state/types"
import { usePurchasesHooks } from "../../../../../purchases"
import { define } from "../../../../../../utils/typeUtils"
import { linkedProductsKey } from "./utils"

export const LinkedProductsPanel = () => {
    const { userHasPermission } = useSystemStateHooks()
    const readOnly = !userHasPermission(UIPermission.SAVE_PRODUCTS)
    const { getLinkedProductIds } = usePurchasesHooks()

    const [linkedProductsData, setLinkedProductsData] = useState<Data[]>([])
    const [currentLinkedProducts, setCurrentLinkedProducts] = useState<Data>()
    const [products, setProducts] = useState<Product[]>([])

    const productLoaders = useProductLoaders()

    const { submitLinkProducts, submitUnlinkProducts } = useActions()
    const { openModal } = useUIActions()

    const load = async () => {
        const { loadAllProducts } = productLoaders()
        const loadedProducts = await loadAllProducts()
        setProducts(loadedProducts)
        const linkedProducts = loadedProducts.filter(product => getLinkedProductIds().includes(product.id))
        const defaultData = linkedProducts.map(product => (product.equivalences || []).map(equivalence => {
            const product_2 = loadedProducts.find(loadedProduct => loadedProduct.id === equivalence.productId)
            return {
                key: linkedProductsKey(product.id, equivalence.productId),
                productId_1: product.id,
                productId_2: equivalence.productId,
                unitTypeId_1: product.unitTypeId,
                unitTypeId_2: define(product_2).unitTypeId,
                equivalence: equivalence.equivalence
            }
        })).flat()
        setLinkedProductsData(defaultData)
    }

    useEffect(() => {
        load()
    }, [])

    const productsList = useMemo(() => products.filter(product => product.type !== ProductType.PACKAGE), [products])

    const rows = useMemo(() => linkedProductsData.map(linkedProductData => ({
        ...linkedProductData,
        equivalenceData: linkedProductData?.key
    })), [linkedProductsData])

    const onAddLinkedProducts = () => {
        openModal(ModalId.LINKED_PRODUCTS_FORM)
        setCurrentLinkedProducts(undefined)
    }

    const onEditLinkedProducts = (key: string) => {
        setCurrentLinkedProducts(linkedProductsData.find(data => data.key === key))
        openModal(ModalId.LINKED_PRODUCTS_FORM)
    }

    const onRemoveLinkedProducts = (key: string) => {
        const newLinkedProductsData = linkedProductsData.filter(data => data.key !== key)
        setLinkedProductsData(newLinkedProductsData)
        submitUnlinkProducts(key)
    }

    const fields: EditableFormField[] = [
        {
            name: 'linkedProducts',
            type: FieldType.TABLE,
            table: TableName.PRODUCTS,
            disabled: true,
            render: () => (
                <LinkedProductsTable
                    rows={rows}
                    readOnly={readOnly}
                    onAdd={onAddLinkedProducts}
                    onEditRow={onEditLinkedProducts}
                    onRemoveRow={onRemoveLinkedProducts}
                />
            )
        }
    ]

    const onSaveLinkedProduct = (newData: Data) => {
        const dataMatches = (data: Data) => data.key === newData.key
        const dataExists = !!linkedProductsData.find(dataMatches)
        const newLinkedProductsData = dataExists ?
            linkedProductsData.map(linkedProductData => dataMatches(linkedProductData) ? newData : linkedProductData) :
            [newData, ...linkedProductsData]
        setLinkedProductsData(newLinkedProductsData)
        submitLinkProducts(newData)
    }

    return (
        <>
            <EditablePanel
                fields={fields}
                readOnly={readOnly}
            />
            <LinkedProductsFormModal
                linkedProducts={currentLinkedProducts}
                productsList={productsList}
                onSubmit={onSaveLinkedProduct}
            />
        </>
    )
}
